/** A YouTube or Vimeo link, resolved to the player it embeds. */
export type VideoEmbed = {
  provider: "YouTube" | "Vimeo";
  /** The iframe address; privacy-enhanced where the provider offers it. */
  src: string;
  /** A vertical video (a YouTube Short), shown in a portrait frame. */
  portrait: boolean;
};

const YOUTUBE_ID = /^[\w-]{11}$/;

/** Seconds in `90`, `90s`, `1m30s` or `1h2m3s`; 0 when it isn't a time. */
export function timestamp(value: string): number {
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/i.exec(value.trim());
  if (!match || !value.trim()) return 0;
  const [, h = "0", m = "0", s = "0"] = match;
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
}

/**
 * Reads the links people paste: youtube.com/watch?v=, youtu.be/, /shorts/,
 * /embed/ and /live/, and vimeo.com/123 (with an unlisted hash) or
 * player.vimeo.com/video/123. Anything else returns null.
 *
 * `loop` asks for a silent loop: muted autoplay, looping, no controls.
 * YouTube still shows its title and logo (it no longer lets embeds hide
 * them); Vimeo's background mode hides everything on paid plans.
 */
export function videoEmbed(link: string, { loop = false } = {}): VideoEmbed | null {
  let url: URL;
  try {
    url = new URL(link.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.replace(/^(www|m|music)\./, "");
  const parts = url.pathname.split("/").filter(Boolean);

  let youtube: string | undefined;
  if (host === "youtu.be") youtube = parts[0];
  else if (host === "youtube.com" || host === "youtube-nocookie.com")
    youtube = ["shorts", "embed", "live"].includes(parts[0])
      ? parts[1]
      : (url.searchParams.get("v") ?? undefined);
  if (youtube && YOUTUBE_ID.test(youtube)) {
    const start = timestamp(url.searchParams.get("t") ?? url.searchParams.get("start") ?? "");
    return {
      provider: "YouTube",
      src: `https://www.youtube-nocookie.com/embed/${youtube}?rel=0${start > 0 ? `&start=${start}` : ""}${
        // A single video loops only as a one-item playlist of itself.
        // cc_load_policy=0 asks for captions off; viewer settings can win.
        loop
          ? `&autoplay=1&mute=1&loop=1&playlist=${youtube}&controls=0&playsinline=1&cc_load_policy=0`
          : ""
      }`,
      portrait: host !== "youtu.be" && parts[0] === "shorts",
    };
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    // showcase/123/video/456 and channels/x/456 embed the video, not the showcase.
    const video = parts.indexOf("video");
    const index =
      video >= 0 && /^\d+$/.test(parts[video + 1] ?? "")
        ? video + 1
        : parts.findIndex((part) => /^\d+$/.test(part));
    if (index < 0) return null;
    const time = timestamp(url.hash.match(/[#&]t=([^&]+)/)?.[1] ?? "");
    // Unlisted videos carry their hash as the next segment or as ?h=.
    const hash = url.searchParams.get("h") ?? parts[index + 1];
    return {
      provider: "Vimeo",
      src: `https://player.vimeo.com/video/${parts[index]}?dnt=1${hash && /^[\da-f]+$/i.test(hash) ? `&h=${hash}` : ""}${loop ? "&background=1" : ""}${time > 0 ? `#t=${time}s` : ""}`,
      portrait: false,
    };
  }
  return null;
}
